import React, { useRef, useState } from 'react';
import produce from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import './NotificationsModal.scss';
import { RequestedApprovers } from './RequestedApprovers';
import { UpdatePageModel } from '../UpdatePageModel';
import { EmailsList, ErrorMessage, IEmailsListAPI } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { AsyncStatus } from 'enums';
import { parseAxiosError } from 'services';

interface INotificationsModalProps {
    canEdit: boolean,
    model: UpdatePageModel,
    show: boolean,
    onHide: () => void,
}

interface INotificationsModalState {
    emails: string[],
    approverIDs: string[],
    saveError?: string,
    saveStatus?: AsyncStatus,
}

export function NotificationsModal({ canEdit, model, show, onHide }: INotificationsModalProps) {
    if (model.loadStatus !== AsyncStatus.Success) return null;

    const [state, setState] = useState<INotificationsModalState>({
        emails: model.update.notificationEmails,
        approverIDs: model.update.requestedApproverIDs,
    });
    const emailsListRef = useRef<IEmailsListAPI>();

    const {
        approverIDs,
        emails,
        saveError,
        saveStatus,
    } = state;

    function onChangeRequestedApprovers(newIDs) {
        setState(produce(draft => {
            draft.approverIDs = newIDs;
        }));
    }

    function onChangeEmails(newEmails: string[]) {
        setState(produce(draft => {
            draft.emails = newEmails;
        }));
    }

    function onSave() {
        const emailsState = emailsListRef.current.finishEditing();
        if (!emailsState.isValid) return;

        setState(produce(draft => {
            draft.saveStatus = AsyncStatus.Loading;
            draft.saveError = '';
        }));

        const updateBefore = cloneDeep(model.update);
        model.update.notificationEmails = emailsState.emails;
        model.update.requestedApproverIDs = approverIDs;
        model.save(updateBefore)
            .then(() => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Success;
                }));

                onHide();
            })
            .catch((err) => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Error;
                    draft.saveError = parseAxiosError(err);
                }));
            });
    }

    return (
        <Modal show={show} onClose={onHide} size="lg" className="notificationsModal">
            <ModalHeader>
                Notifications
            </ModalHeader>
            <ModalBody style={{textAlign: "left"}}>
                <div className="formItem">
                    <div className="formItem_label">
                        Request Approvers
                    </div>
                    <p className="notificationsModal_infoBlock">
                        When you submit this update, any approvers listed here will be notified that it's ready for review.
                    </p>
                    <RequestedApprovers
                        approverIDs={approverIDs}
                        disabled={!canEdit}
                        onChange={onChangeRequestedApprovers}
                    />
                </div>
                <div className="formItem">
                    <div className="formItem_label">
                        Email Notifications List
                    </div>
                    <p className="notificationsModal_infoBlock">
                        You can add email addresses to be notified when this update is deployed.
                        They'll receive an email the day before the update starts, when it starts, and when it finishes.
                    </p>
                    <EmailsList
                        disabled={!canEdit}
                        emails={emails}
                        onChangeEmails={onChangeEmails}
                        ref={emailsListRef}
                    />
                </div>
                {saveError && <ErrorMessage message={saveError} />}
            </ModalBody>
            <ModalFooter>
                <Button
                    variant={"outlined"}
                    onClick={onHide}
                    disabled={saveStatus === AsyncStatus.Loading}
                >
                    Close
                </Button>
                <Button
                    style={{marginLeft: "10px"}}
                    onClick={onSave}
                    loading={saveStatus === AsyncStatus.Loading}
                    disabled={!canEdit || saveStatus === AsyncStatus.Success}
                >
                    {saveStatus === AsyncStatus.Loading ? 'Saving' : 'Save'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}